import { StyledSubtitle, withAuthLayout } from '@/components/auth/AuthLayout'
import UtilButton from '@/components/chat/ChatPane/UtilButton'
import { saveLoginTokenToCookie } from '@/components/utils/Auth'
import { useLoggedInUser } from '@/components/utils/LoggedInUserProvider'
import useDetectOldBrowser from '@/components/utils/useDetectOldBrowser'
import themes from '@/data/themes'
// import { AuthClient } from '@/types/index'
import { CLIENT, GRANT_PASSWORD_CLIENT_ID } from '@/utils/getEnv'
import useSignup from '@/utils/hooks/v3/auth/useSignup'
import { isValidPassword } from '@/utils/index'
import { Checkbox, CircularProgress, FormControl, FormControlLabel, FormHelperText, TextField, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { NextPage } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
import { Controller, FieldValues, SubmitHandler, useForm, UseFormReturn, useWatch } from 'react-hook-form'
import styled from 'styled-components'
import useTermsAndConditionsModal from '../../components/signup/useTermsAndConditionsModal'

interface SignUpFormType extends FieldValues {
  name: string,
  email: string,
  password: string,
  phoneNumber: string,
  termsAndConditions: boolean
}

const SignupPage: NextPage = () => {
  const { control, handleSubmit, setValue } = useForm<SignUpFormType>()
  const { ErrorOldBrowser } = useDetectOldBrowser()
  const { signUp, signedUpData, signingUp, errorSignUp }= useSignup()
  // const [grantType, _] = useState<AuthClient>('password')
  const [_loggedInUser, setLoggedInUser] = useLoggedInUser()
  const { TermsAndConditionsModal, openTermsAndCondtitionsModal }= useTermsAndConditionsModal()
  const [isAccepted, setIsAccepted] = useState(false)
  const router = useRouter()

  useEffect(() => {
    if (router.query.email) {
      setValue('email', router.query.email as string)
    }
  }, [])

  useEffect(() => {
    if (signedUpData){
      setLoggedInUser({ ...signedUpData?.globalUser })
      saveLoginTokenToCookie(signedUpData.jwt.token)
      const token = router.query.token
      if (token) {
        router.replace({
          pathname: '/accept-invite',
          query: { token }
        })
      } else {
        Router.push('/signup/success')
      }
    }
  }, [signedUpData])

  const onSubmit: SubmitHandler<SignUpFormType> = async (data: SignUpFormType, event: React.BaseSyntheticEvent | undefined) => {
    event?.preventDefault()
    let invitation: any | undefined = undefined
    if (router.query.token) {
      invitation = {
        token: router.query.token
      }
    }

    await signUp({ ...data, clientId: GRANT_PASSWORD_CLIENT_ID }, invitation)

  }

  return (
    <>
      <Head>
        <title>Sign up | {themes[CLIENT]?.head?.text || 'Fondasi'}</title>
      </Head>
      <Container>
        <div>
          <h2>Sign Up</h2>
          <StyledSubtitle>Enter your personal details to create an account</StyledSubtitle>
        </div>
        <ErrorAlert>
          {ErrorOldBrowser}
          {
            errorSignUp ?
              <Alert severity='error'>{errorSignUp.error}</Alert> :
              null
          }
        </ErrorAlert>
        <form id="signup" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name={'name'}
            rules={{ required: 'Full Name is required' }}
            render={({ field, fieldState: { error } }) => {
              return <TextField
                id="signup-name"
                autoComplete='name'
                variant="outlined" margin="dense" required
                disabled={signingUp} label="Full Name" type="text"
                error={error !== undefined} helperText={error?.message}
                onChange={field.onChange} value={field.value || ''} fullWidth />
            }}
          />
          <Controller
            control={control}
            name={'email'}
            rules={{ required: 'Email is required' }}
            render={({ field, fieldState: { error } }) => {
              return <TextField
                id="signup-email"
                autoComplete='email'
                variant="outlined" margin="dense" required
                error={error !== undefined} helperText={error?.message}
                disabled={signingUp || !!router.query.email} label="Email" type="email"
                onChange={field.onChange} value={field.value || ''} fullWidth />
            }}
          />
          <Controller
            control={control}
            name={'phoneNumber'}
            rules={{
              required: 'Phone Number is required',
              pattern: {
                value: /\+[0-9]{9,14}/i,
                message: 'Phone number must be valid phone number, start with +' }
            }}
            render={({ field, fieldState: { error } }) => {
              return <TextField
                id="signup-mobile"
                autoComplete='mobile'
                variant="outlined" margin="dense" required
                error={error !== undefined} helperText="Input your phone with '+' sign and country code. i.e: +6288877766655"
                disabled={signingUp} label="Your Phone Number" type="tel"
                onChange={field.onChange} value={field.value || ''} fullWidth />
            }}
          />
          <Controller
            control={control}
            name={'password'}
            rules={{ required: 'Password is required', validate: (value) => !isValidPassword(value) ? 'Your password must at least have 8 characters in length, 1 upper case letter, 1 lower case letter, 1 special character, 1 number' : undefined }}
            render={({ field, fieldState: { error } }) => {
              return <TextField
                id="signup-new-password"
                autoComplete='new-password'
                variant="outlined" margin="dense" required
                disabled={false} label="Password" type="password"
                error={error !== undefined} helperText={error?.message}
                onChange={field.onChange} value={field.value || ''} fullWidth />
            }}
          />
          <Controller
            name="termsAndConditions"
            control={control}
            rules={{ required: `You must accept ${CLIENT === 'smartfren' ? '1ENGAGE' : 'Fondasi' } Terms and Conditions to signup` }}
            render={({ field: controlField, fieldState: { error } }) => {
              return <>
                <FormControl error={!!error}>
                  <FormControlLabel control={<Checkbox {...controlField}
                    onChange={(e) => {
                      controlField.onChange(e.target.checked)
                      setIsAccepted(e.target.checked)
                      setValue('termsAndConditions',e.target.checked, { shouldValidate: true })
                      return e.target.checked
                    }}
                    checked={controlField.value || isAccepted}
                    color="primary"
                    disabled={signingUp}
                  />}
                  label={
                    <>
                      {
                        CLIENT === 'smartfren' ?
                          <p>
                            I accept <b style={{ textDecoration: 'underline' }} onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              openTermsAndCondtitionsModal()
                            }}>
                              1ENGAGE Terms and Conditions
                            </b>
                          </p> :
                          <p>
                            I accept Fondasi <a href="https://www.bahasa.ai/terms-of-services" target="_blank">Terms of service</a> and <a href="https://www.bahasa.ai/privacy-policy" target="_blank">Privacy policy</a>
                          </p>
                      }
                    </>
                  } />
                  <StyledFormHelperText style={{ padding: 16, marginTop: -28 }}>{error?.message}</StyledFormHelperText>
                </FormControl>
              </>
            }}
          />
          <ButtonSignUp control={control} signingUp={signingUp || false} />
        </form>
        <LoginLink>
          <p>Have an account? &nbsp;</p><Link href="/login"><Typography color="primary">Login</Typography></Link>
        </LoginLink>
        <TermsAndConditionsModal setIsAccept={(isAccept) => {
          setValue('termsAndConditions', isAccept, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
          setIsAccepted(isAccept)
        }} />
      </Container>
    </>
  )
}

export default withAuthLayout(SignupPage)

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  /* padding: 32px; */
  max-width: 500px;
  @media (max-width: 800px) {
    padding: 0;
  }
`

const LoginLink = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
  margin-right: auto;

  font-family: 'Trueno';
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 22px;
  color: #000000;
  margin-top: 8px;
  .MuiTypography-root {
    font-size: inherit;
    cursor: pointer;
    font-weight: bold;
  }
`

const ErrorAlert = styled.div`
  align-self: center;
  justify-self: center;
`

const StyledFormHelperText = styled(FormHelperText)`
  color: #f44336;
`


const ButtonSignUp: FC<{ signingUp: boolean, control: UseFormReturn<SignUpFormType>['control'] }> = ({ signingUp, control }) => {
  const watchAll = useWatch({ control })
  const keys = Object.keys(watchAll)

  const disabled = keys.reduce((res, name) => {
    return res || !watchAll[name]
  }, false) // check smua fields ada isinya
  || keys.length < 5 // check smua fields dirty

  return (
    <UtilButton disabled={signingUp || disabled} fullWidth variant="contained" color='primary' type="submit">
      {
        signingUp && <CircularProgress size={20} style={{ color: '#FFF' }} />
      }
              &nbsp;
      <span>
        {
          signingUp ? 'SIGNING UP..' : 'SIGN UP'
        }
      </span>
    </UtilButton>
  )
}